import { Link, useLocation } from "react-router-dom";
import {
  BookOpen,
  ChartColumn,
  ChartNoAxesCombined,
  ChevronRight,
  ChevronsUpDown,
  CircleQuestionMark,
  Globe,
  // GraduationCap, — see the help-videos icon, switched off below.
  KeyRound,
  LogOut,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldCheck,
  Store,
  Sun,
  UserCog,
  UserMinus,
  UserPlus,
  UserRound,
  MapPin,
  MapPinHouse,
  MapPinned,
  Menu as MenuIcon,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Avatar } from "@/components/avatar";
import { Logo, Mark } from "@/components/brand";
import { IconButton } from "@/components/ui/button";
import { Menu, MenuItem, MenuSeparator, menuItemClass } from "@/components/ui/menu";
import { Tooltip } from "@/components/ui/tooltip";
import { SearchInput } from "@/components/ui/search-input";
import type { TranslationKey } from "@/constants/i18n";
import { can, hiddenFrom } from "@/utils/permissions";
import type { Profile } from "@/meta/types";
import { useTranslation } from "@/store/locale";
import { type Gate, areaGate, pageGate } from "@/constants/nav-permissions";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useThemeStore } from "@/store/theme";
import { COMING_SOON, type ComingSoonSlug } from "@/pages/ComingSoonPage";
import { cn } from "@/utils/utils";

export { PageHeader } from "@/components/ui/page";

/**
 * Where the recorded walkthroughs live.
 *
 * Its own host, outside this app and outside its router, which is why it is a
 * plain `href` and a named constant rather than a route: nothing here can link
 * to it by path, and the address is the only thing that would ever change.
 *
 * Switched off along with the icon that used it — see the top bar below.
 */
// const HELP_VIDEOS_URL = "http://18.142.132.11:8080/p/mrm/";

/**
 * The menu as designed. `to` is set only where a route exists, so the rest
 * render as coming-soon pages rather than dead links.
 */
type AppPath =
  | "/"
  | "/provinces"
  | "/cities"
  | "/barangays"
  | "/configuration"
  | "/employees"
  | "/yellow-page"
  | "/non-members"
  | "/member-analytics"
  | "/member-population"
  | "/club-recommendation"
  | "/members";

interface NavItem {
  label: TranslationKey;
  icon: typeof ChartColumn;
  to?: AppPath;
  slug?: ComingSoonSlug;
  /**
   * Whether this row belongs on screen: may the records be read, and has the
   * screen been hidden from this person. Read from `nav-permissions.ts` rather
   * than spelled here, so the menu, the route guard and the Roles grid cannot
   * drift apart.
   *
   * One field rather than two lists, because two lists could not say what
   * Configuration needs: its row opens a rail of ten sections, and it belongs
   * in the menu while *any* of them does. An any-of list of hiding codenames
   * would have taken the row away as soon as one section was hidden, and a
   * list that was never consulted left it there when all ten were.
   */
  gate?: Gate;
}

/** A parent row that goes nowhere itself and opens onto the pages under it. */
interface NavGroup {
  label: TranslationKey;
  icon: typeof ChartColumn;
  children: readonly NavItem[];
}

type NavEntry = NavItem | NavGroup;

const isNavGroup = (entry: NavEntry): entry is NavGroup => "children" in entry;

/**
 * The menu, in the order it is read.
 *
 * One flat list, with the two families that are genuinely a set of places
 * rather than a place — the geography tables, and the maps — folded behind a
 * parent row. Nine rows at rest; the rest are one click away and stay open
 * once you are inside them.
 */
const NAV: readonly NavEntry[] = [
  { label: "nav.dashboard", icon: ChartColumn, to: "/" },
  {
    label: "nav.members",
    icon: UserPlus,
    to: "/members",
    gate: areaGate("members"),
  },
  {
    label: "nav.memberAnalytics",
    icon: ChartNoAxesCombined,
    to: "/member-analytics",
    gate: areaGate("member-analytics"),
  },
  {
    label: "nav.nonMembers",
    icon: UserMinus,
    to: "/non-members",
    gate: areaGate("non-members"),
  },
  {
    label: "nav.employees",
    icon: UserCog,
    to: "/employees",
    gate: areaGate("employees"),
  },
  {
    label: "nav.locationConfig",
    icon: MapPinned,
    children: [
      {
        label: "nav.provinces",
        icon: Globe,
        to: "/provinces",
        gate: pageGate("provinces"),
      },
      {
        label: "nav.cities",
        icon: MapPinHouse,
        to: "/cities",
        gate: pageGate("cities"),
      },
      {
        label: "nav.barangays",
        icon: MapPin,
        to: "/barangays",
        gate: pageGate("barangays"),
      },
    ],
  },
  {
    label: "nav.maps",
    icon: MapPinned,
    children: [
      {
        label: "nav.memberPopulation",
        icon: MapPinned,
        to: "/member-population",
        gate: pageGate("member-population"),
      },
      {
        label: "nav.clubRecommendation",
        icon: Store,
        to: "/club-recommendation",
        gate: pageGate("club-recommendation"),
      },
    ],
  },
  {
    label: "nav.yellowPage",
    icon: BookOpen,
    to: "/yellow-page",
    gate: areaGate("yellow-page"),
  },
  {
    label: "nav.configuration",
    icon: Settings,
    to: "/configuration",
    gate: areaGate("configuration"),
  },
];

/**
 * Whether the parent rows should fold their children open inside the menu
 * rather than float them beside it.
 *
 * Below `md` the sidebar is a drawer as wide as the screen, so a panel placed
 * off its right edge lands off the screen — the children were there, mostly
 * past the glass. The viewport is the right thing to read for once: what
 * decides this is whether the sidebar is a drawer, which is a media query the
 * layout is already making.
 */
const STACKED = "(max-width: 47.99rem)";

const useStackedNav = () => useMediaQuery(STACKED);

const pathOf = (item: NavItem) => item.to ?? `/${item.slug ?? ""}`;
const ALL_ITEMS: readonly NavItem[] = NAV.flatMap((entry) =>
  isNavGroup(entry) ? entry.children : [entry],
);

export function AppShell({
  profile,
  onSignOut,
  children,
}: {
  profile: Profile;
  onSignOut: () => void;
  children: ReactNode;
}) {
  const t = useTranslation();
  const { pathname } = useLocation();
  // Collapsed lasts the tab; a rail nobody asked for on every load is a nag.
  const [collapsed, setCollapsed] = useState(false);
  // Below md the sidebar is a drawer: opened from the top bar, closed by a tap
  // outside, Escape, or following any link in it.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filter, setFilter] = useState("");
  // Which parent rows are folded open. A group opens when you are on a page
  // inside it and stays open until you fold it yourself.
  // Only one flyout stands open at a time, and none does at rest — a panel that
  // opened itself would cover the page you just arrived on.
  const [openGroup, setOpenGroup] = useState<TranslationKey | null>(null);
  const stacked = useStackedNav();

  // A group folded open on a phone would spring out as a flyout the moment the
  // window passed `md`, beside a sidebar nobody is pointing at. Either shape
  // starts shut when the other one has been left behind.
  useEffect(() => setOpenGroup(null), [stacked]);

  // Hover opens a panel; leaving closes it, but not at once. The panel sits a
  // few pixels off the sidebar, and a pointer crossing that gap has left the row
  // without having reached the panel — closing on the instant would snatch the
  // menu away mid-reach. The grace period ends the moment either half is entered.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const openNow = (label: TranslationKey) => {
    holdOpen();
    setOpenGroup(label);
  };
  const closeSoon = () => {
    holdOpen();
    closeTimer.current = setTimeout(() => setOpenGroup(null), 180);
  };
  useEffect(() => holdOpen, []);

  // A click anywhere but on a group's own row or its panel puts the panel away,
  // as does Escape. `data-nav-group` marks both halves, since the panel is
  // portalled out of the sidebar and is nowhere near the row in the DOM.
  useEffect(() => {
    if (!openGroup) return;
    const dismiss = (event: Event) => {
      const outside =
        event instanceof KeyboardEvent
          ? event.key === "Escape"
          : !(event.target as HTMLElement).closest("[data-nav-group]");
      if (outside) setOpenGroup(null);
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", dismiss);
    };
  }, [openGroup]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setDrawerOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const needle = filter.trim().toLowerCase();

  // The box in the sidebar filters the menu. A search field that searched
  // nothing was the one control here that did not do what it looked like.
  const entries = useMemo(() => {
    const matches = (label: TranslationKey) => t(label).toLowerCase().includes(needle);
    // What the person may see comes first; a door they cannot open is not
    // listed, rather than listed and refused.
    // Two questions in one: may they read it, and has anybody said it does not
    // belong in their interface. A role given `view__club` for a dropdown
    // somewhere else can be given `hide__club` too, and Business Unit leaves
    // the menu while every picker fed by clubs goes on working.
    const allow = (...codenames: string[]) => can(profile, ...codenames);
    const hidden = (...codenames: string[]) => hiddenFrom(profile, ...codenames);
    const allowed = (item: NavItem) => !item.gate || item.gate(allow, hidden);

    return NAV.flatMap<NavEntry>((entry) => {
      if (!isNavGroup(entry)) {
        return allowed(entry) && matches(entry.label) ? [entry] : [];
      }
      const children = entry.children.filter(allowed);
      if (children.length === 0) return [];
      // Typing the parent's name keeps everything under it; otherwise only the
      // children that match are listed, and the parent comes along to hold them.
      const kept = matches(entry.label) ? children : children.filter((c) => matches(c.label));
      return kept.length ? [{ ...entry, children: kept }] : [];
    });
  }, [needle, t, profile]);

  const current = useCurrentPageName(pathname);

  return (
    <div
      className={cn(
        // The shell is exactly the viewport and does not scroll; the content
        // column does. The sidebar is `h-dvh`, so a scrolling body slid the page
        // past the end of it and left a band of bare background under it.
        "grid h-dvh overflow-hidden",
        collapsed ? "md:grid-cols-[72px_1fr]" : "md:grid-cols-[223px_1fr]",
      )}
    >
      <a
        href="#main"
        className="bg-accent text-accent-fg text-callout sr-only rounded-lg px-4 py-2 font-medium focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-50"
      >
        {t("shell.skipToContent")}
      </a>

      {drawerOpen ? (
        <button
          type="button"
          aria-label={t("shell.closeMenu")}
          onClick={() => setDrawerOpen(false)}
          className="bg-scrim fixed inset-0 z-30 md:hidden"
        />
      ) : null}

      <aside
        aria-label={t("shell.mainNav")}
        className={cn(
          "bg-sidebar border-sidebar-line fixed inset-y-0 left-0 z-40 flex w-[240px] max-w-[85vw] flex-col border-r",
          "transition-transform duration-[var(--duration-slow)] ease-[var(--ease-out-soft)]",
          drawerOpen ? "translate-x-0" : "-translate-x-full",
          "md:sticky md:top-0 md:h-dvh md:w-auto md:max-w-none md:translate-x-0",
        )}
      >
        <div
          className={cn(
            "flex h-16 shrink-0 items-center gap-2 pr-3 pl-4",
            collapsed && "md:justify-center md:px-0",
          )}
        >
          <Link
            to="/"
            title={t("shell.system")}
            className="flex min-w-0 flex-1 items-center rounded-lg"
          >
            {collapsed ? (
              <Mark className="h-7 shrink-0" />
            ) : (
              <Logo className="h-9 w-[100px] shrink-0" />
            )}
            <span className="sr-only">{t("shell.system")}</span>
          </Link>
          <IconButton
            label={t("shell.closeMenu")}
            size="icon-sm"
            className="md:hidden"
            onClick={() => setDrawerOpen(false)}
          >
            <X aria-hidden />
          </IconButton>
          <IconButton
            label={collapsed ? t("shell.expand") : t("shell.collapse")}
            size="icon-sm"
            aria-pressed={collapsed}
            className={cn("hidden md:inline-flex", collapsed && "md:hidden")}
            onClick={() => setCollapsed(true)}
          >
            <PanelLeftClose aria-hidden />
          </IconButton>
        </div>

        {collapsed ? (
          <div className="hidden shrink-0 justify-center pb-2 md:flex">
            <IconButton
              label={t("shell.expand")}
              size="icon-sm"
              aria-pressed={collapsed}
              onClick={() => setCollapsed(false)}
            >
              <PanelLeftOpen aria-hidden />
            </IconButton>
          </div>
        ) : (
          <div className="shrink-0 px-3 pb-1">
            <SearchInput
              fieldSize="sm"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              onClear={() => setFilter("")}
              placeholder={t("shell.search")}
              aria-label={t("shell.navSearch")}
            />
          </div>
        )}

        <nav
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain px-3 pt-3 pb-4"
          onClick={(event) => {
            if (!(event.target as HTMLElement).closest("a")) return;
            setDrawerOpen(false);
            setOpenGroup(null);
          }}
        >
          {entries.map((entry) =>
            isNavGroup(entry) ? (
              <NavGroupRow
                key={entry.label}
                group={entry}
                pathname={pathname}
                collapsed={collapsed}
                open={openGroup === entry.label}
                stacked={stacked}
                // Search lists its hits in place instead: the matches are the
                // point of the query, and a panel you must open first hides them.
                filtering={Boolean(needle)}
                onHoverOpen={() => openNow(entry.label)}
                onHoverClose={closeSoon}
                onToggle={() => {
                  holdOpen();
                  setOpenGroup((current) => (current === entry.label ? null : entry.label));
                }}
              />
            ) : (
              <NavLink
                key={pathOf(entry)}
                item={entry}
                active={pathname === pathOf(entry)}
                collapsed={collapsed}
                label={t(entry.label)}
              />
            ),
          )}
          {entries.length === 0 ? (
            <p className="text-fg-subtle text-subhead shrink-0 px-2.5 py-3">
              {t("shell.noNavMatch")}
            </p>
          ) : null}
        </nav>

        {/* Who is signed in, at the foot of the menu rather than adrift in the
            top bar: it belongs with the rest of the things that are yours. */}
        <div className="border-sidebar-line shrink-0 border-t p-2">
          <AccountMenu profile={profile} onSignOut={onSignOut} collapsed={collapsed} />
        </div>
      </aside>

      <div className="flex h-dvh min-w-0 flex-col overflow-hidden">
        {/* Above the scroller rather than sticky inside it: it is a fixed part
            of the frame now, not something the content slides under. */}
        <header className="bg-surface-overlay border-line z-20 flex min-h-[61px] min-w-0 shrink-0 items-center gap-3 border-b px-4 py-2.5 backdrop-blur-xl md:px-6">
          <IconButton
            label={t("shell.openMenu")}
            className="md:hidden"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon aria-hidden />
          </IconButton>
          <nav
            aria-label="Breadcrumb"
            className="text-fg-muted text-subhead flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden whitespace-nowrap"
          >
            <span className="hidden md:inline">{t("shell.system")}</span>
            <ChevronRight className="hidden size-3.5 shrink-0 md:inline" aria-hidden />
            <span className="text-fg truncate font-medium" aria-current="page">
              {current}
            </span>
          </nav>
          {/* The ways of asking for help, held together at the right edge.
              `ml-auto` lives on the group rather than on any one control, so
              the edge holds whichever of them render — the help desk is hidden
              from a person without the right, and used to carry the margin
              itself. The breadcrumb already takes the rest of the row. */}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            {/* Recorded walkthroughs, which live outside the app entirely:
                a new tab, so whatever somebody was in the middle of here is
                still here when they come back. Ungated — it explains the
                system rather than reaching into it.

                Off for now, at request. Restoring it is this block, the
                `GraduationCap` import, and the `HELP_VIDEOS_URL` constant,
                each commented in the same way; `nav.helpVideos` is still in
                both dictionaries and the tests are `describe.skip`.

            <Tooltip label={t("nav.helpVideos")} side="bottom" align="end">
              <a
                href={HELP_VIDEOS_URL}
                target="_blank"
                rel="noreferrer noopener"
                aria-label={t("nav.helpVideos")}
                className="text-fg-muted hover:bg-surface-sunken hover:text-fg grid size-9 shrink-0 place-items-center rounded-lg transition-colors"
              >
                <GraduationCap className="size-5" aria-hidden />
              </a>
            </Tooltip>
            */}

            {/* The help desk, reachable from wherever somebody is when the
                thing they need help with happens — which is the whole reason
                it is up here and not only a row in the menu. Hidden rather
                than disabled for a person without the right: it is not a door
                that opens later. */}
            {areaGate("helpdesk")(
              (...codenames) => can(profile, ...codenames),
              (...codenames) => hiddenFrom(profile, ...codenames),
            ) ? (
              <Tooltip label={t("nav.helpdesk")} side="bottom" align="end">
                <Link
                  to="/helpdesk"
                  aria-label={t("nav.helpdesk")}
                  aria-current={pathname.startsWith("/helpdesk") ? "page" : undefined}
                  className={cn(
                    "grid size-9 shrink-0 place-items-center rounded-lg transition-colors",
                    pathname.startsWith("/helpdesk")
                      ? "bg-accent-soft text-accent"
                      : "text-fg-muted hover:bg-surface-sunken hover:text-fg",
                  )}
                >
                  <CircleQuestionMark className="size-5" aria-hidden />
                </Link>
              </Tooltip>
            ) : null}
          </div>
        </header>
        {/* The one scroller. `min-h-0` so it may be shorter than its content
            rather than stretching the column past the viewport. */}
        {/* `isolate`: every z-index a page uses — sticky table headers, filter
            panels — stays inside the page, so nothing on it can paint over the
            header's menu, whatever number it picks. */}
        {/* `data-page-scroller`: this column, not the document, is what has a
            scrollbar on a shell route, so it is the one an overlay has to hold
            still — see `holdPage` in `ui/dialog`. */}
        <main
          id="main"
          data-page-scroller
          className="isolate min-h-0 min-w-0 flex-1 overflow-y-auto"
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * One row of the menu.
 *
 * The selected row is a filled accent pill: on a grey sidebar a tint alone is
 * too quiet to find at a glance, and where you are is the first question the
 * navigation has to answer.
 */
function NavLink({
  item,
  active,
  collapsed,
  label,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  label: string;
}) {
  const className = cn(
    "flex h-9 shrink-0 items-center gap-2.5 rounded-lg px-2.5 text-callout",
    "transition-[background-color,color] duration-[var(--duration-fast)]",
    collapsed && "md:justify-center md:px-0",
    // Tinted rather than filled, the way every other selected thing in the app
    // reads — the tabs, the account chips, the dashboard's icon squares. A
    // solid accent bar is louder than the page it is pointing at.
    active
      ? "bg-accent-soft text-accent font-medium [&_svg]:text-accent"
      : "text-sidebar-fg hover:bg-sidebar-hover [&_svg]:text-sidebar-icon",
  );
  const body = (
    <>
      <item.icon className="size-4.5 shrink-0" aria-hidden />
      {collapsed ? (
        <span className="sr-only">{label}</span>
      ) : (
        <span className="truncate">{label}</span>
      )}
    </>
  );
  const props = {
    title: label,
    className,
    "aria-current": active ? ("page" as const) : undefined,
  };
  return item.to ? (
    <Link to={item.to} {...props}>
      {body}
    </Link>
  ) : (
    <Link to={`/${item.slug ?? ""}`} {...props}>
      {body}
    </Link>
  );
}

/**
 * A parent row, and the pages under it as a panel beside the sidebar.
 *
 * The children open *next to* the menu rather than inside it, so the rows below
 * never move and the spacing down the sidebar is the same whatever is open. The
 * panel is portalled to the body: the sidebar is a transformed, scrolling box,
 * which would otherwise both clip it and make `fixed` mean "fixed to the aside".
 *
 * The parent is not a link — there is no "all provinces and cities and
 * barangays" page for it to lead to — so it is a button that opens the panel.
 */
function NavGroupRow({
  group,
  pathname,
  collapsed,
  open,
  stacked,
  filtering,
  onHoverOpen,
  onHoverClose,
  onToggle,
}: {
  group: NavGroup;
  pathname: string;
  collapsed: boolean;
  open: boolean;
  stacked: boolean;
  filtering: boolean;
  onHoverOpen: () => void;
  onHoverClose: () => void;
  onToggle: () => void;
}) {
  const t = useTranslation();
  const label = t(group.label);
  const holdsCurrentPage = group.children.some((child) => pathOf(child) === pathname);

  const rowRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  // Two shapes for the same children. On a phone, and while the list is being
  // searched, they belong in the menu itself; everywhere else they float.
  const folded = stacked || filtering;
  const showPanel = open && !folded;

  // Measured rather than guessed, and re-measured while it is open: the menu
  // list scrolls under the panel, and the window can be resized beneath it.
  useLayoutEffect(() => {
    if (!showPanel) return;
    const place = () => {
      const row = rowRef.current;
      if (!row) return;
      const rect = row.getBoundingClientRect();
      const sidebar = row.closest("aside")?.getBoundingClientRect();
      const height = panelRef.current?.offsetHeight ?? 0;
      setAt({
        // Off the sidebar's edge, not the row's, so every panel lines up.
        left: (sidebar?.right ?? rect.right) + 8,
        top: Math.max(8, Math.min(rect.top - 6, window.innerHeight - height - 8)),
      });
    };
    place();
    window.addEventListener("resize", place);
    // Capture: the scroller is the menu list, not the document.
    document.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      document.removeEventListener("scroll", place, true);
    };
  }, [showPanel]);

  /** Only a real pointer hovers; a tap opens the panel through the click. */
  const hovering = useRef(false);
  // Nothing hovers a folded row open: a fold moves the rows below it, and a
  // pointer merely crossing the menu would have them shifting under it.
  const hover = folded
    ? {}
    : {
        onPointerEnter: (event: React.PointerEvent) => {
          if (event.pointerType !== "mouse") return;
          hovering.current = true;
          onHoverOpen();
        },
        onPointerLeave: (event: React.PointerEvent) => {
          if (event.pointerType !== "mouse") return;
          hovering.current = false;
          onHoverClose();
        },
      };

  const children = group.children.map((child) => (
    <NavLink
      key={pathOf(child)}
      item={child}
      active={pathname === pathOf(child)}
      collapsed={false}
      label={t(child.label)}
    />
  ));

  return (
    <div data-nav-group className="flex shrink-0 flex-col gap-0.5">
      <button
        ref={rowRef}
        type="button"
        title={label}
        onClick={() => {
          // With a mouse the hover has already opened this panel, and the click
          // that follows naturally would otherwise shut it again the instant it
          // appeared. Touch and the keyboard never hover, so for them it toggles.
          if (hovering.current && open) return;
          onToggle();
        }}
        {...hover}
        aria-expanded={open}
        className={cn(
          "text-callout flex h-9 w-full shrink-0 cursor-pointer items-center gap-2.5 rounded-lg px-2.5",
          "transition-[background-color,color] duration-[var(--duration-fast)]",
          collapsed && "md:justify-center md:px-0",
          holdsCurrentPage
            ? "bg-accent-soft text-accent [&_svg]:text-accent font-medium"
            : "text-sidebar-fg hover:bg-sidebar-hover [&_svg]:text-sidebar-icon",
          // Held down while its panel is up, so it reads as the open one.
          showPanel && !holdsCurrentPage && "bg-sidebar-hover",
        )}
      >
        <group.icon className="size-4.5 shrink-0" aria-hidden />
        {collapsed ? (
          <span className="sr-only">{label}</span>
        ) : (
          <>
            <span className="min-w-0 flex-1 truncate text-left">{label}</span>
            <ChevronRight
              className={cn(
                "size-4 shrink-0 transition-transform duration-[var(--duration-fast)]",
                // Pointing at what it opens: sideways at a panel beside the
                // menu, down at the rows it has just unfolded below itself.
                stacked && open && "rotate-90",
              )}
              aria-hidden
            />
          </>
        )}
      </button>

      {filtering || (stacked && open) ? (
        <div
          role="group"
          aria-label={label}
          className={cn(
            "flex shrink-0 flex-col gap-0.5 pl-3",
            // The search results are already there when the query lands; only
            // a fold the person opened has an opening to play.
            !filtering && "animate-fold",
          )}
        >
          {children}
        </div>
      ) : null}

      {showPanel
        ? createPortal(
            <div
              ref={panelRef}
              data-nav-group
              role="group"
              aria-label={label}
              {...hover}
              style={at ? { top: at.top, left: at.left } : undefined}
              className={cn(
                "border-line bg-surface-raised fixed z-50 w-56 rounded-xl border p-1.5",
                "max-h-[calc(100dvh-16px)] overflow-y-auto overscroll-contain",
                "animate-sheet origin-left shadow-[var(--shadow-float)]",
                "flex flex-col gap-0.5",
                // Hidden until the first measurement lands. Reopening reuses the
                // last position, which the layout effect corrects before paint.
                at ? null : "invisible",
              )}
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** The breadcrumb's last crumb: the name of whatever is on screen. */
function useCurrentPageName(pathname: string) {
  const t = useTranslation();
  const slug = pathname.slice(1);
  const navLabel = ALL_ITEMS.find((item) => pathOf(item) === pathname)?.label;
  if (navLabel) return t(navLabel);
  // The entry holds a translation key, so it is translated like every other
  // crumb here rather than printed raw.
  if (slug in COMING_SOON) return t(COMING_SOON[slug].title);
  // The help desk has no sidebar row — it is reached from the top bar — so
  // there is no nav item for the lookup above to find it by.
  if (pathname === "/helpdesk") return t("nav.helpdesk");
  if (pathname.startsWith("/helpdesk/")) return t("hd.ticket");
  if (pathname === "/employee-activity-log") return t("page.activityLog");
  if (pathname === "/member-activity-log") return t("page.memberActivityLog");
  if (pathname.startsWith("/members/")) return t("page.memberDetail");
  if (pathname === "/yellow-page-categories") return t("page.yellowPageCategories");
  if (pathname === "/profile") return t("page.profile");
  if (pathname === "/profile/password") return t("page.changePassword");
  return t("nav.dashboard");
}

/**
 * The person's own corner at the foot of the menu: who is signed in, what is
 * theirs to change — profile, password, theme — the way out, and Roles.
 *
 * Roles is the odd one: it is not a personal setting but an administrator's
 * screen, and it used to be the ninth section of the Configuration rail. It is
 * here because this is where it was asked for, and it reads well enough beside
 * the rest — who may do what, next to who you are. It is gated the way a
 * sidebar row is, so somebody without `view__group` never sees the line.
 */
function AccountMenu({
  profile,
  onSignOut,
  collapsed,
}: {
  profile: Profile;
  onSignOut: () => void;
  collapsed: boolean;
}) {
  const t = useTranslation();
  const name = profile.full_name ?? profile.user_name;
  const showRoles = areaGate("roles")(
    (...codenames) => can(profile, ...codenames),
    (...codenames) => hiddenFrom(profile, ...codenames),
  );

  // Two choices, so the row shows the current one and a click takes the other.
  const themeChoice = useThemeStore((state) => state.choice);
  const setTheme = useThemeStore((state) => state.setChoice);
  const theme = themeChoice ?? "light";

  return (
    <Menu
      side="top"
      align="start"
      width="w-[232px]"
      trigger={
        <span
          title={t("shell.accountMenu")}
          className={cn(
            "hover:bg-sidebar-hover flex items-center gap-2.5 rounded-lg p-1.5 transition-colors",
            collapsed && "md:justify-center md:px-0",
          )}
        >
          <Avatar src={profile.avatar} name={name} className="size-8 shrink-0" />
          {collapsed ? (
            <span className="sr-only">{name}</span>
          ) : (
            <>
              <span className="min-w-0 flex-1 text-left">
                <span className="text-fg text-subhead block truncate font-medium">{name}</span>
                <span className="text-fg-muted text-footnote block truncate">
                  {profile.user_name}
                </span>
              </span>
              <ChevronsUpDown className="text-fg-subtle size-4 shrink-0" aria-hidden />
            </>
          )}
        </span>
      }
    >
      <div className="flex items-center gap-3 px-2.5 py-2">
        <Avatar src={profile.avatar} name={name} className="size-9" />
        <span className="min-w-0">
          <span className="text-fg text-callout block truncate font-medium">{name}</span>
          <span className="text-fg-muted text-subhead block truncate">{profile.user_name}</span>
        </span>
      </div>
      <MenuSeparator />
      <Link to="/profile" className={menuItemClass()}>
        <UserRound aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t("shell.viewProfile")}</span>
      </Link>
      <Link to="/profile/password" className={menuItemClass()}>
        <KeyRound aria-hidden />
        <span className="min-w-0 flex-1 truncate">{t("shell.changePassword")}</span>
      </Link>
      {showRoles ? (
        <>
          <MenuSeparator />
          <Link to="/roles" className={menuItemClass()}>
            <ShieldCheck aria-hidden />
            <span className="min-w-0 flex-1 truncate">{t("nav.roleConfiguration")}</span>
          </Link>
        </>
      ) : null}
      <MenuSeparator />
      <MenuItem
        icon={theme === "dark" ? <Moon aria-hidden /> : <Sun aria-hidden />}
        trailing={theme === "dark" ? t("theme.darkValue") : t("theme.lightValue")}
        data-keep-open
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        {t("shell.theme")}
      </MenuItem>
      <MenuSeparator />
      <MenuItem danger icon={<LogOut aria-hidden />} onClick={onSignOut}>
        {t("shell.logout")}
      </MenuItem>
    </Menu>
  );
}
